/**
 * GoPay MoR — seller policy strip.
 * Opens documents via ApiClient.requestText (tenant headers) → modal HTML viewer.
 * Never uses bare `/api/v1/...` target=_blank (loses X-Tenant-Code).
 */
import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomerLanguageToggle } from '../../app/CustomerLanguageToggle.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { ApiError } from '../../shared/api/ApiClient.js';
import {
  fetchStorePolicyDocumentHtml,
  useTenantStorePoliciesQuery,
} from './storePoliciesApi.js';
import type { TenantStorePolicyKind } from './storePoliciesTypes.js';

const KIND_I18N: Record<TenantStorePolicyKind, string> = {
  TERMS: 'morChrome.policies.terms',
  PRIVACY: 'morChrome.policies.privacy',
  RETURNS_AND_COMPLAINTS: 'morChrome.policies.returns',
  WITHDRAWAL_RIGHTS: 'morChrome.policies.withdrawal',
};

export interface SellerPolicyStripProps {
  readonly apiTenantCode?: string | null;
  readonly testIdPrefix?: string;
  /** Show language toggle (policy surfaces). */
  readonly showLanguageToggle?: boolean;
}

export function SellerPolicyStrip({
  apiTenantCode = null,
  testIdPrefix = 'customer-seller-policies',
  showLanguageToggle = false,
}: SellerPolicyStripProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const api = useApiClient();
  const routeTenantCode = useTenantCode();
  const tenantCode =
    typeof apiTenantCode === 'string' && apiTenantCode.trim().length > 0
      ? apiTenantCode.trim()
      : routeTenantCode;
  const query = useTenantStorePoliciesQuery(apiTenantCode);
  const data = query.data;
  const titleId = useId();

  const [viewerKind, setViewerKind] = useState<TenantStorePolicyKind | null>(null);
  const [viewerHtml, setViewerHtml] = useState<string | null>(null);
  const [viewerBusy, setViewerBusy] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);

  const closeViewer = useCallback((): void => {
    setViewerKind(null);
    setViewerHtml(null);
    setViewerError(null);
    setViewerBusy(false);
  }, []);

  useEffect(() => {
    if (viewerKind == null) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeViewer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewerKind, closeViewer]);

  const openPolicy = useCallback(
    async (kind: TenantStorePolicyKind): Promise<void> => {
      if (viewerBusy || !hasTenant(tenantCode)) {
        return;
      }
      setViewerKind(kind);
      setViewerHtml(null);
      setViewerError(null);
      setViewerBusy(true);
      try {
        const html = await fetchStorePolicyDocumentHtml(api, tenantCode, kind);
        setViewerHtml(html);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : t('morChrome.policies.loadFailed', {
                defaultValue: 'Could not load this policy document. Retry in a moment.',
              });
        setViewerError(message);
      } finally {
        setViewerBusy(false);
      }
    },
    [api, tenantCode, t, viewerBusy],
  );

  if (data == null || !data.published || data.policies.length === 0) {
    return null;
  }

  return (
    <section
      className="flex min-w-0 flex-col gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3"
      data-testid={testIdPrefix}
      aria-label={t('morChrome.policies.ariaLabel')}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-[var(--color-on-surface-muted)]">
          {t('morChrome.policies.heading')}
        </h3>
        {showLanguageToggle ? (
          <CustomerLanguageToggle
            dataTestId={`${testIdPrefix}-language`}
            placement="inline"
          />
        ) : null}
      </div>
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0" data-testid={`${testIdPrefix}-links`}>
        {data.policies.map((policy) => {
          const label =
            policy.label != null && policy.label.trim().length > 0
              ? policy.label
              : t(KIND_I18N[policy.kind]);
          return (
            <li key={policy.kind}>
              <button
                type="button"
                className="cursor-pointer border-0 bg-transparent p-0 text-left text-sm text-[var(--color-accent)] underline underline-offset-2"
                data-testid={`${testIdPrefix}-${policy.kind.toLowerCase()}`}
                onClick={() => {
                  void openPolicy(policy.kind);
                }}
              >
                {label}
              </button>
            </li>
          );
        })}
      </ul>

      {viewerKind != null ? (
        <div
          className="fixed inset-0 z-[var(--z-modal,50)] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          data-testid={`${testIdPrefix}-viewer-backdrop`}
        >
          <button
            type="button"
            className="absolute inset-0 cursor-default border-0 bg-transparent p-0"
            aria-label={t('morChrome.policies.close', { defaultValue: 'Close' })}
            data-testid={`${testIdPrefix}-viewer-backdrop-dismiss`}
            onClick={closeViewer}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg"
            data-testid={`${testIdPrefix}-viewer`}
          >
            <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-3">
              <h2 id={titleId} className="m-0 text-base font-semibold text-[var(--color-on-surface)]">
                {t(KIND_I18N[viewerKind])}
              </h2>
              <button
                type="button"
                className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm"
                data-testid={`${testIdPrefix}-viewer-close`}
                onClick={closeViewer}
              >
                {t('morChrome.policies.close', { defaultValue: 'Close' })}
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-4">
              {viewerBusy ? (
                <p className="m-0 text-sm text-[var(--color-on-surface-muted)]" role="status">
                  {t('morChrome.policies.loading', { defaultValue: 'Loading…' })}
                </p>
              ) : null}
              {viewerError != null ? (
                <p className="m-0 text-sm text-[var(--color-danger)]" role="alert">
                  {viewerError}
                </p>
              ) : null}
              {viewerHtml != null ? (
                <iframe
                  title={t(KIND_I18N[viewerKind])}
                  className="h-[min(70vh,36rem)] w-full border-0 bg-white"
                  sandbox=""
                  srcDoc={viewerHtml}
                  data-testid={`${testIdPrefix}-viewer-frame`}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function hasTenant(tenantCode: string | null | undefined): tenantCode is string {
  return typeof tenantCode === 'string' && tenantCode.trim().length > 0;
}
